"use client";

import {
  Building2,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import {
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError } from "@/lib/api";
import { createPublicLead } from "@/lib/leads";
import { lookupPostalCode } from "@/lib/postal-code";
import type { PublicProduct } from "@/types/store";

type RealEstateFinancingModalProps = {
  companySlug: string;
  products: PublicProduct[];
  initialProductId?: number | null;
  onClose: () => void;
};

const inputClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatCep(value: string) {
  const digits = onlyDigits(value).slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, "$1-$2");
}

function formatCpf(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(
      /^(\d{3})\.(\d{3})\.(\d{3})(\d)/,
      "$1.$2.$3-$4",
    );
}

export default function RealEstateFinancingModal({
  companySlug,
  products,
  initialProductId = null,
  onClose,
}: RealEstateFinancingModalProps) {
  const availableProducts = useMemo(
    () => products.filter((product) => product.is_available),
    [products],
  );

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [occupation, setOccupation] = useState("");

  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isLookingUpPostalCode, setIsLookingUpPostalCode] =
    useState(false);
  const [postalCodeMessage, setPostalCodeMessage] =
    useState("");

  const [productId, setProductId] = useState(
    initialProductId ? String(initialProductId) : "",
  );
  const [propertyValue, setPropertyValue] = useState("");
  const [hasDownPayment, setHasDownPayment] = useState(false);
  const [downPaymentAmount, setDownPaymentAmount] =
    useState("");
  const [useFgts, setUseFgts] = useState(false);
  const [desiredTermMonths, setDesiredTermMonths] =
    useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    setProductId(
      initialProductId ? String(initialProductId) : "",
    );
  }, [initialProductId]);

  useEffect(() => {
    const digits = onlyDigits(zipCode);

    if (digits.length !== 8) {
      setPostalCodeMessage("");
      return;
    }

    let cancelled = false;

    async function loadPostalCode() {
      setIsLookingUpPostalCode(true);
      setPostalCodeMessage("");

      try {
        const result = await lookupPostalCode(digits);

        if (cancelled) return;

        setStreet(result.address || "");
        setNeighborhood(result.district || "");
        setCity(result.city || "");
        setState(result.state || "");

        if (
          result.complement &&
          !addressComplement.trim()
        ) {
          setAddressComplement(result.complement);
        }

        setPostalCodeMessage(
          "Endereço localizado automaticamente.",
        );
      } catch {
        if (cancelled) return;

        setPostalCodeMessage(
          "Não foi possível localizar esse CEP. Preencha o endereço manualmente.",
        );
      } finally {
        if (!cancelled) {
          setIsLookingUpPostalCode(false);
        }
      }
    }

    void loadPostalCode();

    return () => {
      cancelled = true;
    };
  }, [zipCode]);

  useEffect(() => {
    if (!productId) {
      setPropertyValue("");
      return;
    }

    const selectedProduct = availableProducts.find(
      (product) => product.id === Number(productId),
    );

    setPropertyValue(
      selectedProduct?.current_price ?? "",
    );
  }, [productId, availableProducts]);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const selectedProduct = availableProducts.find(
        (product) => product.id === Number(productId),
      );

      await createPublicLead(companySlug, {
        name,
        phone,
        email,
        source: "store",
        lead_type: "real_estate_financing",
        product_id: selectedProduct?.id,
        details: {
          cpf: onlyDigits(cpf),
          birth_date: birthDate,
          monthly_income: monthlyIncome,
          occupation,
          zip_code: onlyDigits(zipCode),
          street,
          address_number: addressNumber,
          address_complement: addressComplement,
          neighborhood,
          city,
          state: state.trim().toUpperCase(),
          property_of_interest: selectedProduct?.name || "",
          property_value: propertyValue,
          has_down_payment: hasDownPayment,
          down_payment_amount: hasDownPayment
            ? downPaymentAmount
            : "",
          use_fgts: useFgts,
          desired_term_months: Number(desiredTermMonths),
        },
      });

      setIsSuccess(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(
          error.message ||
            "Não foi possível enviar a solicitação.",
        );
      } else {
        setErrorMessage(
          "Não foi possível enviar a solicitação.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSuccess) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <CheckCircle2
            size={52}
            className="mx-auto text-emerald-500"
          />

          <h2 className="mt-5 text-2xl font-black text-slate-950">
            Solicitação enviada
          </h2>

          <p className="mt-3 text-sm leading-6 text-slate-500">
            A imobiliária recebeu seus dados e poderá entrar
            em contato com você.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-7 h-11 w-full rounded-xl bg-blue-600 text-sm font-bold text-white transition hover:bg-blue-700"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-5"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-5 sm:px-7">
          <div>
            <div className="flex items-center gap-2 text-blue-600">
              <Building2 size={20} />
              <span className="text-xs font-black uppercase tracking-[0.16em]">
                Imóveis
              </span>
            </div>

            <h2 className="mt-2 text-2xl font-black text-slate-950">
              Financiamento imobiliário
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Preencha seus dados para solicitar uma simulação.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto"
        >
          <div className="space-y-7 p-5 sm:p-7">
            <section>
              <h3 className="text-sm font-black text-slate-900">
                Seus dados
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label>
                  <span className={labelClass}>Nome *</span>
                  <input
                    required
                    value={name}
                    onChange={(event) =>
                      setName(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    WhatsApp *
                  </span>
                  <input
                    required
                    inputMode="tel"
                    value={phone}
                    onChange={(event) =>
                      setPhone(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label className="sm:col-span-2">
                  <span className={labelClass}>E-mail *</span>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-black text-slate-900">
                Dados para financiamento
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label>
                  <span className={labelClass}>CPF *</span>
                  <input
                    required
                    inputMode="numeric"
                    value={cpf}
                    onChange={(event) =>
                      setCpf(formatCpf(event.target.value))
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Data de nascimento *
                  </span>
                  <input
                    required
                    type="date"
                    min="1900-01-01"
                    max={new Date()
                      .toISOString()
                      .slice(0, 10)}
                    value={birthDate}
                    onChange={(event) =>
                      setBirthDate(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Renda mensal *
                  </span>
                  <input
                    required
                    inputMode="decimal"
                    value={monthlyIncome}
                    onChange={(event) =>
                      setMonthlyIncome(event.target.value)
                    }
                    className={inputClass}
                    placeholder="Ex.: 8500,00"
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Profissão *
                  </span>
                  <input
                    required
                    value={occupation}
                    onChange={(event) =>
                      setOccupation(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-black text-slate-900">
                Endereço residencial
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label>
                  <span className={labelClass}>CEP *</span>
                  <input
                    required
                    inputMode="numeric"
                    value={zipCode}
                    onChange={(event) =>
                      setZipCode(formatCep(event.target.value))
                    }
                    className={inputClass}
                    placeholder="00000-000"
                  />

                  {isLookingUpPostalCode ? (
                    <span className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                      <Loader2
                        size={12}
                        className="animate-spin"
                      />
                      Consultando CEP...
                    </span>
                  ) : postalCodeMessage ? (
                    <span className="mt-1.5 block text-xs text-slate-500">
                      {postalCodeMessage}
                    </span>
                  ) : null}
                </label>

                <label>
                  <span className={labelClass}>
                    Número *
                  </span>
                  <input
                    required
                    value={addressNumber}
                    onChange={(event) =>
                      setAddressNumber(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label className="sm:col-span-2">
                  <span className={labelClass}>
                    Endereço / Logradouro *
                  </span>
                  <input
                    required
                    value={street}
                    onChange={(event) =>
                      setStreet(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Complemento
                  </span>
                  <input
                    value={addressComplement}
                    onChange={(event) =>
                      setAddressComplement(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Bairro *
                  </span>
                  <input
                    required
                    value={neighborhood}
                    onChange={(event) =>
                      setNeighborhood(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Cidade *
                  </span>
                  <input
                    required
                    value={city}
                    onChange={(event) =>
                      setCity(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>UF *</span>
                  <input
                    required
                    maxLength={2}
                    value={state}
                    onChange={(event) =>
                      setState(
                        event.target.value.toUpperCase(),
                      )
                    }
                    className={inputClass}
                  />
                </label>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-black text-slate-900">
                Imóvel e condições
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className={labelClass}>
                    Imóvel de interesse *
                  </span>

                  <select
                    required
                    value={productId}
                    onChange={(event) =>
                      setProductId(event.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">
                      Selecione o imóvel
                    </option>

                    {availableProducts.map((product) => (
                      <option
                        key={product.id}
                        value={product.id}
                      >
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span className={labelClass}>
                    Valor do imóvel *
                  </span>
                  <input
                    required
                    readOnly
                    value={propertyValue}
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    Prazo desejado (meses) *
                  </span>
                  <input
                    required
                    type="number"
                    min={1}
                    max={420}
                    value={desiredTermMonths}
                    onChange={(event) =>
                      setDesiredTermMonths(
                        event.target.value,
                      )
                    }
                    className={inputClass}
                    placeholder="Ex.: 360"
                  />
                </label>

                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input
                    type="checkbox"
                    checked={hasDownPayment}
                    onChange={(event) =>
                      setHasDownPayment(
                        event.target.checked,
                      )
                    }
                  />
                  <span className="text-sm font-bold text-slate-700">
                    Possuo valor de entrada
                  </span>
                </label>

                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input
                    type="checkbox"
                    checked={useFgts}
                    onChange={(event) =>
                      setUseFgts(event.target.checked)
                    }
                  />
                  <span className="text-sm font-bold text-slate-700">
                    Pretendo utilizar FGTS
                  </span>
                </label>

                {hasDownPayment ? (
                  <label className="sm:col-span-2">
                    <span className={labelClass}>
                      Valor da entrada *
                    </span>
                    <input
                      required
                      inputMode="decimal"
                      value={downPaymentAmount}
                      onChange={(event) =>
                        setDownPaymentAmount(
                          event.target.value,
                        )
                      }
                      className={inputClass}
                      placeholder="Ex.: 80000,00"
                    />
                  </label>
                ) : null}
              </div>
            </section>

            {errorMessage ? (
              <div className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className="sticky bottom-0 border-t border-slate-100 bg-white px-5 py-4 sm:px-7">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    size={18}
                    className="animate-spin"
                  />
                  Enviando...
                </>
              ) : (
                "Solicitar simulação"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
